import {
  formatCalendarDay,
  foldSessionMonthAgenda,
  monthKeyFromDate,
} from "../utils/sessionCalendarAgenda";

/** Fictitious TTC Belvédère — filled into the real screens only while Pingflo walks an empty club. */

export const DEMO_CLUB = {
  name: "TTC Belvédère",
  hall: "Salle des Cèdres",
};

export const DEMO_GROUPS = [
  { id: "tour-demo-cadets", name: "Cadets A", players: 12, coach: "Camille Roux" },
  { id: "tour-demo-espoirs", name: "Espoirs", players: 9, coach: "Marc Morel" },
  { id: "tour-demo-club", name: "Club night", players: 18, coach: "Camille Roux" },
];

export const DEMO_PLAYERS = [
  { id: "p1", name: "Léa Martin", sessions: 11, exercises: 42, hours: 9.5 },
  { id: "p2", name: "Noah Dubois", sessions: 10, exercises: 38, hours: 8.0 },
  { id: "p3", name: "Inès Bernard", sessions: 9, exercises: 33, hours: 7.5 },
  { id: "p4", name: "Hugo Lefèvre", sessions: 8, exercises: 29, hours: 6.5 },
  { id: "p5", name: "Chloé Petit", sessions: 7, exercises: 24, hours: 5.5 },
];

function upcomingIso(weekdayIndex, daysAhead = 0) {
  const now = new Date();
  const day = now.getDay();
  let delta = (weekdayIndex - day + 7) % 7;
  if (delta === 0) delta = 7;
  const d = new Date(now);
  d.setDate(now.getDate() + delta + daysAhead);
  return formatCalendarDay(d);
}

export function demoHomeGroups() {
  return DEMO_GROUPS.map((g) => ({
    gr_id: g.id,
    gr_name: g.name,
    totalPlayers: g.players,
    totalWomen: Math.max(2, Math.round(g.players / 3)),
    femalePlayers: Math.max(2, Math.round(g.players / 3)),
    totalSessions: 14,
    remainingSessions: 8,
    groupDetails: {
      gr_id: g.id,
      gr_name: g.name,
      gr_image: null,
      club_image: null,
    },
  }));
}

export function demoGroupListRows() {
  return DEMO_GROUPS.map((g, index) => ({
    id: index + 1,
    coachCount: 1,
    playerCount: g.players,
    femalePlayerCount: Math.max(2, Math.round(g.players / 3)),
    totalSessions: 14,
    group: {
      gr_id: g.id,
      gr_name: g.name,
      gr_traning_session_type: { ts_gen_mode: "coach" },
    },
  }));
}

export function demoDashboardCounts() {
  return {
    totalUsers: 48,
    monthlyActivePlayers: 31,
    totalCoaches: 4,
    totalGroups: 6,
    totalTrainingGroups: 6,
    totalPlayers: 48,
    totalSessions: 22,
    coachGroupsCount: 6,
    sessionsFinalizedThisYear: 22,
    uniquePlayersPresent: 31,
    sessionsScheduledUpcoming: 4,
    playerGroupsCount: 2,
    sessionsPerformedThisYear: 12,
    sessionsScheduledThisYear: 3,
    trainingExercisesExecuted: 48,
    lastWeekTrainingHours: 3.5,
    womenPlayers: 18,
  };
}

export function demoParticipationWeeks() {
  return [
    { label: "W1", participants: 16 },
    { label: "W2", participants: 21 },
    { label: "W3", participants: 19 },
    { label: "W4", participants: 24 },
  ];
}

function demoSessionRow({
  ts_id,
  group,
  ts_session_number,
  ts_date,
  ts_start_time,
  ts_owner_player_id = null,
}) {
  return {
    ts_id,
    gr_id: group.id,
    gr_name: group.name,
    ts_session_number,
    ts_date,
    ts_start_time,
    ...(ts_owner_player_id ? { ts_owner_player_id } : {}),
    _coachHomeGroupName: group.name,
  };
}

function demoSessionsInMonth(anchor = new Date()) {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  const last = new Date(y, m + 1, 0).getDate();
  const day = (n) => formatCalendarDay(new Date(y, m, Math.min(n, last)));
  return [
    demoSessionRow({
      ts_id: "tour-demo-s1",
      group: DEMO_GROUPS[0],
      ts_session_number: 4,
      ts_date: day(10),
      ts_start_time: "19:00:00",
    }),
    demoSessionRow({
      ts_id: "tour-demo-s-personal",
      group: DEMO_GROUPS[2],
      ts_session_number: 1,
      ts_date: day(12),
      ts_start_time: "18:00:00",
      ts_owner_player_id: "tour-demo-me",
    }),
    demoSessionRow({
      ts_id: "tour-demo-s-personal-same",
      group: DEMO_GROUPS[2],
      ts_session_number: 2,
      ts_date: day(10),
      ts_start_time: "20:00:00",
      ts_owner_player_id: "tour-demo-me",
    }),
    demoSessionRow({
      ts_id: "tour-demo-s2",
      group: DEMO_GROUPS[1],
      ts_session_number: 5,
      ts_date: day(22),
      ts_start_time: "18:30:00",
    }),
  ];
}

/** Club group nights and player-owned bookings the calendar can mark. */
export function demoPlayerCalendarSessions(anchor = new Date()) {
  const cadets = upcomingIso(2);
  const personal = upcomingIso(3);
  const espoirs = upcomingIso(4);
  const upcoming = [
    demoSessionRow({
      ts_id: "tour-demo-s1",
      group: DEMO_GROUPS[0],
      ts_session_number: 4,
      ts_date: cadets,
      ts_start_time: "19:00:00",
    }),
    demoSessionRow({
      ts_id: "tour-demo-s-personal",
      group: DEMO_GROUPS[2],
      ts_session_number: 1,
      ts_date: personal,
      ts_start_time: "18:00:00",
      ts_owner_player_id: "tour-demo-me",
    }),
    demoSessionRow({
      ts_id: "tour-demo-s-personal-same",
      group: DEMO_GROUPS[2],
      ts_session_number: 2,
      ts_date: cadets,
      ts_start_time: "20:00:00",
      ts_owner_player_id: "tour-demo-me",
    }),
    demoSessionRow({
      ts_id: "tour-demo-s2",
      group: DEMO_GROUPS[1],
      ts_session_number: 5,
      ts_date: espoirs,
      ts_start_time: "18:30:00",
    }),
  ];
  const month = monthKeyFromDate(anchor);
  if (upcoming.some((s) => s.ts_date.startsWith(month))) return upcoming;
  return demoSessionsInMonth(anchor);
}

export function demoCalendarAgendaDays(anchor = new Date()) {
  return foldSessionMonthAgenda(demoPlayerCalendarSessions(anchor));
}

export function demoSessionsForDate(dateKey, anchor = new Date()) {
  if (!dateKey) return [];
  const [y, m] = String(dateKey).split("-").map(Number);
  const monthAnchor =
    Number.isFinite(y) && Number.isFinite(m)
      ? new Date(y, m - 1, 1)
      : anchor;
  return demoPlayerCalendarSessions(monthAnchor).filter((s) => s.ts_date === dateKey);
}

export function demoNextSessions() {
  return demoPlayerCalendarSessions().filter((s) => !s.ts_owner_player_id).slice(0, 2);
}

export function demoSession() {
  return {
    ts_id: "tour-demo-session",
    gr_id: DEMO_GROUPS[0].id,
    gr_name: DEMO_GROUPS[0].name,
    ts_date: upcomingIso(2),
    ts_start_time: "19:00",
    ts_duration: 90,
    ts_session_number: 4,
  };
}

export function demoParentChildren() {
  return [
    {
      child_player_id: "tour-demo-lea",
      first_name: "Léa",
      age: 12,
      user_image: null,
      club_id: "tour-demo-club",
      club_name: "Demo Club",
      companion: { displayName: "Pingwi" },
      has_own_login: false,
      can_open_player_account: false,
      min_self_login_age: 13,
    },
    {
      child_player_id: "tour-demo-hugo",
      first_name: "Hugo",
      age: 9,
      user_image: null,
      club_id: "tour-demo-club-b",
      club_name: "Partner Club",
      companion: { displayName: "Pingflo" },
      has_own_login: false,
      can_open_player_account: false,
      min_self_login_age: 13,
    },
  ];
}

export function demoParentProgress(childId = "tour-demo-lea") {
  const child = demoParentChildren().find((c) => c.child_player_id === childId) || demoParentChildren()[0];
  return {
    child: {
      child_player_id: child.child_player_id,
      first_name: child.first_name,
      medical_notes: "",
      user_image: null,
      companion: child.companion,
      club_id: child.club_id || "tour-demo-club",
      club_name: child.club_name || "Demo Club",
      club_image: null,
    },
    metrics: {
      currentLevel: 3,
      levelTitle: "Club cadet",
      sessionsAttended: 11,
      weeksActive: 6,
      streakWeeks: 4,
      nextMilestoneSessionsRemaining: 1,
    },
    badges: [
      {
        id: "tour-demo-badge",
        icon: "🏓",
        title: "First serve",
        desc: "Ten sessions with the club",
        earned: true,
      },
    ],
    trainingGroups: [{ id: "tour-demo-g1", name: "Cadets A" }],
    nextSessions: [
      {
        id: "tour-demo-parent-s1",
        ts_id: "tour-demo-s1",
        title: "Cadets A",
        group_name: "Cadets A",
        hall: DEMO_CLUB.hall,
        table: "Table 3",
        coachName: "Camille Roux",
        status: "pending",
        date: upcomingIso(2),
        start_time: "19:00",
      },
      {
        id: "tour-demo-parent-s2",
        ts_id: "tour-demo-s2",
        title: "Club night",
        group_name: "Club night",
        hall: DEMO_CLUB.hall,
        table: "Table 1",
        coachName: "Marc Morel",
        status: "pending",
        date: upcomingIso(4),
        start_time: "18:30",
      },
    ],
    coachNotes: [
      {
        id: "tour-demo-note",
        coachName: "Camille Roux",
        message: "Léa's serve is landing long — keep the toss steady.",
        date: `${upcomingIso(2)}T18:00:00`,
      },
    ],
  };
}

export function demoCompetitionView() {
  const home = {
    league_team_id: "tour-demo-home",
    display_name: "TTC Belvédère 1",
    division: "Seniors",
    team_number: 1,
    club_id: "tour-demo-club",
    club_short_name: "Belvédère",
  };
  const away = {
    league_team_id: "tour-demo-away",
    display_name: "TT Metz 1",
    division: "Seniors",
    team_number: 1,
    club_short_name: "Metz",
  };
  return {
    club_id: "tour-demo-club",
    season: { season_label: "2025-2026", round_code: "J4", round_label: "Round 4" },
    my_teams: [home],
    club_teams: [home],
    all_teams: [home, away],
    fixtures: [
      {
        fixture_id: "tour-demo-fx-1",
        match_date: upcomingIso(6),
        matchday: { code: "J4" },
        home,
        away,
        hall: DEMO_CLUB.hall,
      },
    ],
    roster_by_team: [
      {
        team_number: 1,
        players: DEMO_PLAYERS.map((p, i) => ({
          player_id: p.id,
          display_name: p.name,
          classement: String(900 + i * 40),
        })),
      },
    ],
  };
}

export function demoPlanningLanes() {
  return [
    {
      tsg_id: "tour-demo-main",
      name: "Cadets A",
      color: "#0877BC",
      timer_mode: "synced",
      is_main: true,
      tables: [],
      exercises: [
        {
          te_id: "tour-demo-te-1",
          tsg_id: "tour-demo-main",
          te_order: 1,
          te_wave: 1,
          te_duration: "20",
          ex_id: "tour-demo-ex-1",
          ex_name: "Multiball serve",
          ex_name_en: "Multiball serve",
        },
      ],
    },
    {
      tsg_id: "tour-demo-sub",
      name: "Table 4",
      color: "#E85D04",
      timer_mode: "independent",
      is_main: false,
      tables: [],
      exercises: [
        {
          te_id: "tour-demo-te-2",
          tsg_id: "tour-demo-sub",
          te_order: 1,
          te_wave: 1,
          te_duration: "20",
          ex_id: "tour-demo-ex-2",
          ex_name: "Footwork",
          ex_name_en: "Footwork",
        },
      ],
    },
  ];
}

export function isTourDemoId(value) {
  return String(value || "").startsWith("tour-demo");
}

export function demoSmartTrail() {
  return {
    trail: {
      activeDimension: "S",
      status: { S: "complete", M: "partial", A: "empty", R: "empty", T: "empty" },
      labels: {
        S: { letter: "S", label: "Specific" },
        M: { letter: "M", label: "Measurable" },
        A: { letter: "A", label: "Achievable" },
        R: { letter: "R", label: "Relevant" },
        T: { letter: "T", label: "Time-bound" },
      },
      slots: { S: { purposeId: 3, labels: { primaryFocus: "Forehand consistency" } } },
      coachPendingDims: [],
    },
    goals: [
      { attribute_id: "tour-demo-fh", label: "Forehand consistency", is_primary: true },
      { attribute_id: "tour-demo-fw", label: "Footwork to the wide ball", is_primary: false },
    ],
    goalExercises: [
      {
        title: "Multiball wide forehand",
        goalLabel: "Forehand consistency",
        status: "assigned",
        times_performed: 4,
        goalRole: "primary",
        attributeId: "tour-demo-fh",
      },
    ],
    trainingsTodo: [
      { kind: "group", title: "Cadets A · Tuesday 19:00", priority: "high", detail: "Salle des Cèdres" },
    ],
  };
}

export function isEmptyDashboard(data) {
  if (!data) return true;
  return !data.totalUsers && !data.totalGroups && !data.totalCoaches && !data.monthlyActivePlayers;
}
